import React, { useState, useEffect, useMemo } from "react";
import {
  Plus,
  ExternalLink,
  Clock,
  Bookmark,
  FileUser,
  FileEdit,
  Trash2,
  ArrowLeft,
  CircleX,
  CircleCheck,
} from "lucide-react";
import {
  saveJob,
  deleteJob,
  getUserProfile,
  getUserMessage,
  validateJob,
  getMatchScores,
} from "./services/api";
import { useToast } from "./components/ToastContext";
import JobCard from "./components/JobCard";

const splitCompanyNames = (company = "") =>
  company
    .split(";")
    .map((name) => name.trim())
    .filter(Boolean);

const normalizeCompanyName = (company = "") => company.trim().toLowerCase();

const buildCompanyOptions = (jobs) => [
  ...new Set(jobs.flatMap((job) => splitCompanyNames(job.company))),
];

const createJobUrlRow = (url = "") => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  url,
  status: "idle",
  validation: null,
  error: "",
});

const YourJobsScreen = ({
  navigateTo,
  jobList,
  setJobList,
  handleJobApplication,
  userEmail,
  showJobUrlPopup,
  setShowJobUrlPopup,
  customJobUrl,
  setCustomJobUrl,
  isLoading,
  handleTailorResume,
  handleDownloadPdf,
  resetJobUrlPopup,
  resumeData,
  setResumeData,
  setSavedJobCompanies,
  savedJobCompanies,
  isRefreshingScores,
  subscriptionCounts,
  setSubscriptionCounts,
  setJobDescription,
  isLoadingJobs,
  loadSavedJobs,
  handleErrors,
}) => {
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isGettingJobDetails, setIsGettingJobDetails] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [batchLimit, setBatchLimit] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [jobUrlRows, setJobUrlRows] = useState([createJobUrlRow(customJobUrl)]);

  useEffect(() => {
    loadSavedJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function loadResumeChanges() {
      const userInfo = await getUserProfile(
        userEmail,
        false,
        true,
        false,
        true,
      );
      setResumeData(userInfo.resumeData || {});
      if (subscriptionCounts.tier === null) {
        const {
          resumes_used,
          resumes_limit,
          cover_letters_generated,
          cover_letters_limit,
          end_date,
          free_trial_used,
          batch_limit,
          billing_cycle_end,
          tier,
        } = userInfo.subscription;
        setSubscriptionCounts({
          resumes_used: resumes_used,
          resumes_limit: resumes_limit,
          cover_letters_generated: cover_letters_generated,
          cover_letters_limit: cover_letters_limit,
          end_date: end_date,
          free_trial_used: free_trial_used,
          batch_limit: batch_limit,
          billing_cycle_end: billing_cycle_end,
          tier: tier,
        });
        setBatchLimit(userInfo.subscription.batch_limit || 0);
      } else {
        setBatchLimit(subscriptionCounts.batch_limit || 0);
      }
    }

    loadResumeChanges();
  }, [userEmail]);

  useEffect(() => {
    if (showJobUrlPopup) {
      setJobUrlRows([createJobUrlRow(customJobUrl)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showJobUrlPopup]);

  const searchedJobs = useMemo(() => {
    let result = [...jobList];

    if (searchQuery) {
      result = result.filter(
        (job) =>
          job.title?.toLowerCase().includes(searchQuery) ||
          job.company?.toLowerCase().includes(searchQuery) ||
          job.location?.toLowerCase().includes(searchQuery),
      );
    }

    if (filterCompany) {
      const normalizedFilter = normalizeCompanyName(filterCompany);
      result = result.filter((job) =>
        splitCompanyNames(job.company).some(
          (company) => normalizeCompanyName(company) === normalizedFilter,
        ),
      );
    }

    switch (sortBy) {
      case "saved-date":
        result.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        break;
      case "match-score":
        result.sort((a, b) => {
          const aScore =
            a.matchScores && Object.keys(a.matchScores).length > 0
              ? a.matchScores[
                  Object.keys(a.matchScores).reduce((i, j) =>
                    a.matchScores[i] > a.matchScores[j] ? i : j,
                  )
                ]
              : 0;
          const bScore =
            b.matchScores && Object.keys(b.matchScores).length > 0
              ? b.matchScores[
                  Object.keys(b.matchScores).reduce((i, j) =>
                    b.matchScores[i] > b.matchScores[j] ? i : j,
                  )
                ]
              : 0;
          return bScore - aScore;
        });
        break;
      default:
        result.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    }

    return result;
  }, [jobList, searchQuery, filterCompany, sortBy]);

  const handleToggleSelect = (jobId) => {
    setSelectedJobs((prev) =>
      prev.includes(jobId)
        ? prev.filter((id) => id !== jobId)
        : [...prev, jobId],
    );
  };

  const handleDeselectAll = () => {
    setSelectedJobs([]);
  };

  const handleSelectAll = () => {
    setSelectedJobs(searchedJobs.map((job) => job.id));
  };

  const handleBulkDelete = async () => {
    if (selectedJobs.length === 0) {
      toast.info("Please select at least one job.");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete the ${selectedJobs.length} selected job${selectedJobs.length > 1 ? "s" : ""}? This action cannot be undone.`,
    );

    if (!confirmed) return;

    setIsBulkProcessing(true);

    try {
      const deletePromises = selectedJobs.map((jobId) =>
        handleDeleteJob(jobId),
      );
      const results = await Promise.allSettled(deletePromises);

      const failures = results.filter((r) => r.status === "rejected");

      if (failures.length > 0) {
        console.error("Some deletions failed");
        throw new Error(`${failures.length} deletion(s) failed`);
      }

      setJobList((prev) =>
        prev.filter((job) => !selectedJobs.includes(job.id)),
      );
      const count = selectedJobs.length;
      setSelectedJobs([]);

      toast.success(`${count} job${count > 1 ? "s" : ""} deleted.`);
    } catch (error) {
      await handleErrors("Bulk delete jobs", error);
      toast.error("Some deletions failed. Please try again.");
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const getBestResumeName = (job) => {
    const matchScores = job?.matchScores || {};
    const scoreNames = Object.keys(matchScores);
    if (job?.selectedResume) return job.selectedResume;
    if (scoreNames.length > 0) {
      return scoreNames.reduce((a, b) =>
        matchScores[a] > matchScores[b] ? a : b,
      );
    }
    return Object.keys(resumeData || {})[0] || null;
  };

  const bulkTailorJobs = async (
    jobsToTailor,
    { clearSelection = false } = {},
  ) => {
    const tailoredResults = [];

    for (const job of jobsToTailor) {
      if (!job) continue;
      const selectedResume = getBestResumeName(job);

      try {
        const { tailoredData, details } = await handleTailorResume(
          selectedResume,
          { ...job, selectedResume },
          job.description,
        );

        if (!tailoredData?.tailored_resume_base64) {
          throw new Error("Tailoring did not return a resume.");
        }

        await handleDownloadPdf(
          true,
          tailoredData.tailored_resume_docx_base64 ||
            tailoredData.tailored_resume_base64,
          details,
          job.url,
        );

        tailoredResults.push({
          job: job.title,
          company: job.company,
          url: job.url,
          tailoredResume:
            tailoredData.tailored_resume_docx_base64 ||
            tailoredData.tailored_resume_base64,
          matchScore: job.matchScores?.[selectedResume],
          ats: tailoredData.ATS_score,
          success: true,
        });
      } catch (error) {
        await handleErrors("Bulk tailor job", error);
        tailoredResults.push({
          job: job.title,
          company: job.company,
          success: false,
          error: getUserMessage(error),
        });
        continue;
      }

      setJobList((prev) =>
        prev.map((j) => (j.id === job.id ? { ...j, tailored: true } : j)),
      );
      try {
        await saveJob({ ...job, tailored: true }, userEmail);
      } catch (e) {
        console.error("Failed to update saved job after tailoring:", e);
      }
    }

    const successful = tailoredResults.filter((r) => r.success).length;
    const failed = tailoredResults.filter((r) => !r.success).length;

    if (clearSelection) {
      setSelectedJobs([]);
    }

    if (failed === 0) {
      toast.success(
        `Created ${successful} tailored resume${successful > 1 ? "s" : ""}.`,
      );
      navigateTo("history");
    } else if (successful === 0) {
      toast.error(
        "Tailoring failed. Please try the tailor feature directly, or contact support if it keeps failing.",
      );
    } else {
      toast.info(`${successful} tailored, ${failed} failed.`);
      navigateTo("history");
    }

    return tailoredResults;
  };

  const handleBulkTailor = async () => {
    if (selectedJobs.length === 0) {
      toast.info("Please select at least one job.");
      return;
    } else if (selectedJobs.length > batchLimit) {
      toast.error(
        `Your subscription allows up to ${batchLimit} resumes for batch tailoring.`,
      );
      return;
    }

    setIsBulkProcessing(true);

    try {
      const jobsToTailor = selectedJobs
        .map((jobId) => jobList.find((j) => j.id === jobId))
        .filter(Boolean);
      await bulkTailorJobs(jobsToTailor, { clearSelection: true });
    } catch (error) {
      await handleErrors("Bulk tailoring", error);
      toast.error(getUserMessage(error));
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!userEmail) {
      toast.info("Please log in to delete jobs.");
      navigateTo("login");
      return;
    }

    try {
      const response = await deleteJob(jobId, userEmail);
      if ("error" in response) {
        throw new Error("Error deleting job");
      }
    } catch (error) {
      console.error("Failed to delete job. Please try again.");
      await handleErrors("Error deleting job", error);
      throw error;
    } finally {
      const updatedList = jobList.filter((job) => job.id !== jobId);
      setJobList(updatedList);
      setSavedJobCompanies(buildCompanyOptions(updatedList));
      handleFilter("company", "");
    }
  };

  const validJobRows = jobUrlRows.filter(
    (row) => row.status === "valid" && row.validation?.details,
  );
  const maxPopupRows = Math.max(1, batchLimit || 1);
  const isMultiUrlMode = jobUrlRows.length > 1;
  const hasMultipleValidJobs = validJobRows.length > 1;
  const isAnyUrlValidating = jobUrlRows.some(
    (row) => row.status === "validating",
  );

  const buildJobDataFromRow = (row) => {
    const details = row.validation?.details || {};
    return {
      title: details.title || "",
      department: details.department || "",
      company: details.company || "",
      location: details.location || "Location not specified",
      salary: details.salary || "Salary not specified",
      difficulty: row.validation?.automation_difficulty || "Medium",
      url: row.url,
      description: details.description || "",
      matchScore: details.matchScore || 0,
      tailored: details.tailored || false,
    };
  };

  const updateJobUrlRow = (rowId, updater) => {
    setJobUrlRows((prev) =>
      prev.map((row) => (row.id === rowId ? updater(row) : row)),
    );
  };

  const handleJobUrlRowChange = (rowId, url) => {
    updateJobUrlRow(rowId, (row) => ({
      ...row,
      url,
      status: "idle",
      validation: null,
      error: "",
    }));
    if (jobUrlRows[0]?.id === rowId) {
      setCustomJobUrl(url);
    }
  };

  const handleAddJobUrlRow = () => {
    if (jobUrlRows.length >= maxPopupRows) {
      toast.info(
        `Your plan allows up to ${maxPopupRows} job URL${maxPopupRows > 1 ? "s" : ""} at once.`,
      );
      return;
    }
    setJobUrlRows((prev) => [...prev, createJobUrlRow()]);
  };

  const handleRemoveJobUrlRow = (rowId) => {
    setJobUrlRows((prev) => {
      const next =
        prev.length > 1 ? prev.filter((row) => row.id !== rowId) : prev;
      setCustomJobUrl(next[0]?.url || "");
      return next;
    });
  };

  const validateJobUrlRow = async (row) => {
    const url = row.url.trim();
    if (!url) {
      return {
        ...row,
        status: "invalid",
        validation: null,
        error: "Enter a job posting URL.",
      };
    }

    try {
      let result = await validateJob(url);
      if (!result?.details?.description) {
        result = {
          ...result,
          details: { ...(result.details || {}), valid: false },
        };
      }
      if (!result.valid || result.details?.valid === false) {
        return {
          ...row,
          status: "invalid",
          validation: result,
          error:
            result.reason || "We couldn't retrieve a usable job description.",
        };
      }
      return {
        ...row,
        status: "valid",
        validation: result,
        error: "",
      };
    } catch (error) {
      await handleErrors("Job URL Validation", error);
      return {
        ...row,
        status: "invalid",
        validation: null,
        error:
          getUserMessage(error) ||
          "We couldn't retrieve the job description. Please check the URL.",
      };
    }
  };

  const handleValidateJobUrlRows = async () => {
    if (!jobUrlRows.some((row) => row.url.trim())) {
      setJobUrlRows((prev) =>
        prev.map((row) => ({
          ...row,
          status: "invalid",
          error: "Enter a job posting URL.",
        })),
      );
      return;
    }

    setJobUrlRows((prev) =>
      prev.map((row) =>
        row.url.trim()
          ? { ...row, status: "validating", error: "", validation: null }
          : row,
      ),
    );

    const rowsSnapshot = jobUrlRows;
    const results = await Promise.allSettled(
      rowsSnapshot.map(async (row) =>
        row.url.trim() ? validateJobUrlRow(row) : row,
      ),
    );

    const nextRows = results.map((result, index) =>
      result.status === "fulfilled"
        ? result.value
        : {
            ...rowsSnapshot[index],
            status: "invalid",
            validation: null,
            error: "Validation failed. Please try again.",
          },
    );

    setJobUrlRows(nextRows);
    const firstValid = nextRows.find((row) => row.status === "valid");
    if (firstValid) {
      setJobDescription(firstValid.validation.details.description);
    }
  };

  const saveValidJobRows = async (rowsToSave) => {
    const savedJobs = [];
    let duplicateCount = 0;
    let failedCount = 0;

    for (const row of rowsToSave) {
      try {
        const jobData = buildJobDataFromRow(row);
        const response = await saveJob(jobData, userEmail);
        if (response.success) {
          if (response.duplicate || response.modified) {
            duplicateCount += 1;
          }
          if (response.job) {
            let savedJob = response.job;
            if (
              !savedJob.matchScores ||
              Object.keys(savedJob.matchScores).length === 0
            ) {
              try {
                const matchScores = await getMatchScores(
                  userEmail,
                  savedJob.description || jobData.description,
                );
                savedJob = { ...savedJob, matchScores: matchScores || {} };
              } catch (scoreError) {
                await handleErrors(
                  "Generate saved job match scores",
                  scoreError,
                );
              }
            }
            savedJobs.push(savedJob);
          }
        } else {
          failedCount += 1;
          await handleErrors("Failed to save job", response);
        }
      } catch (error) {
        failedCount += 1;
        await handleErrors("Failed to save job", error);
      }
    }

    if (savedJobs.length > 0) {
      const existingIds = new Set(jobList.map((job) => job.id));
      const mergedList = [
        ...jobList.filter(
          (job) => !savedJobs.some((savedJob) => savedJob.id === job.id),
        ),
        ...savedJobs,
      ];
      setJobList(mergedList);
      setSavedJobCompanies(buildCompanyOptions(mergedList));
      const createdCount = savedJobs.filter(
        (job) => !existingIds.has(job.id),
      ).length;
      if (createdCount > 0) {
        toast.success(
          `Saved ${createdCount} job${createdCount > 1 ? "s" : ""}.`,
        );
      }
    }

    if (duplicateCount > 0) {
      toast.info(
        `${duplicateCount} job${duplicateCount > 1 ? "s were" : " was"} already saved.`,
      );
    }
    if (failedCount > 0) {
      toast.error(
        `${failedCount} job${failedCount > 1 ? "s" : ""} could not be saved.`,
      );
    }

    return savedJobs;
  };

  const handleSaveValidJobRows = async () => {
    if (!userEmail) {
      toast.info("Please log in to save jobs.");
      navigateTo("login");
      return [];
    }
    if (validJobRows.length === 0) {
      toast.info("Please scrape at least one valid job URL first.");
      return [];
    }

    setIsSaving(true);
    try {
      const savedJobs = await saveValidJobRows(validJobRows);
      if (savedJobs.length > 0) {
        resetJobUrlPopup();
      }
      return savedJobs;
    } finally {
      setIsSaving(false);
    }
  };

  const handleTailorValidJobRows = async () => {
    if (validJobRows.length === 0) {
      toast.info("Please scrape at least one valid job URL first.");
      return;
    }
    if (validJobRows.length > maxPopupRows) {
      toast.error(
        `Your subscription allows up to ${maxPopupRows} jobs at once.`,
      );
      return;
    }

    setIsSaving(true);
    setIsBulkProcessing(true);
    try {
      const savedJobs = await saveValidJobRows(validJobRows);
      if (savedJobs.length === 0) return;
      resetJobUrlPopup();
      await bulkTailorJobs(savedJobs);
    } finally {
      setIsSaving(false);
      setIsBulkProcessing(false);
    }
  };

  const handleTailorResumeViaUrl = async () => {
    if (validJobRows.length > 1) {
      await handleTailorValidJobRows();
      return;
    }

    const validRow = validJobRows[0];
    setIsGettingJobDetails(true);
    if (!validRow?.validation?.details?.description) {
      toast.info(
        "We couldn't read the job description automatically. Please paste it on the next screen.",
      );
    } else {
      setJobDescription(validRow.validation.details.description);
    }
    resetJobUrlPopup();
    setIsGettingJobDetails(false);
    navigateTo("tailor");
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleFilter = (field, value = "") => {
    switch (field) {
      case "company":
        if (value !== "") {
          setFilterCompany(value);
        } else {
          setFilterCompany("");
        }
        break;
      default:
        setFilterCompany("");
    }
  };

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  return (
    <div className="bg-loading-color">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="applicant-theme-text">Your Saved Jobs</h2>
            {!isLoadingJobs && searchedJobs.length > 0 && (
              <button
                onClick={() => setShowJobUrlPopup(true)}
                className="buttons-indigo"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Job link
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <input
              type="text"
              placeholder="Search..."
              onChange={handleSearchInputChange}
              className="p-2 py-2 border rounded-lg text-center"
            />
            <select
              className="dropdown-select"
              value={filterCompany}
              onChange={(e) => handleFilter("company", e.target.value)}
            >
              <option value="">All Companies</option>
              {savedJobCompanies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
            <select
              className="dropdown-select"
              onChange={(e) => handleSortChange(e.target.value)}
            >
              <option value="">Sort By</option>
              <option value="saved-date">Saved Date (Newest to Oldest)</option>
              <option value="match-score">
                Match Score (Highest to Lowest)
              </option>
            </select>
          </div>
          {isRefreshingScores && (
            <div className="container-center h-full">
              Refreshing match scores behind the scenes...
            </div>
          )}
        </div>

        {showJobUrlPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <button
                  onClick={() => {
                    resetJobUrlPopup();
                    setShowJobUrlPopup(true);
                  }}
                  className="buttons-navigation"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h3 className="text-lg font-semibold text-gray-900">
                  Add Custom Job URL
                </h3>
                <button onClick={resetJobUrlPopup} className="buttons-red-mini">
                  <CircleX className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Job Posting URL{isMultiUrlMode ? "s" : ""}
                    </label>
                    <button
                      type="button"
                      onClick={handleAddJobUrlRow}
                      disabled={jobUrlRows.length >= maxPopupRows}
                      className="buttons-indigo-mini"
                      title={`Add another URL. Limit: ${maxPopupRows}`}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    {jobUrlRows.map((row, index) => (
                      <div key={row.id} className="mb-2-top">
                        {index === 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            Paste the URL of the job posting you want to apply
                            to
                          </p>
                        )}
                        <div className="flex gap-2 items-center">
                          <input
                            type="url"
                            value={row.url}
                            onChange={(e) =>
                              handleJobUrlRowChange(row.id, e.target.value)
                            }
                            placeholder="https://company.com/jobs/position-id"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                          {(isMultiUrlMode || row.status === "validating") && (
                            <div className="w-8 flex justify-center">
                              {row.status === "validating" && (
                                <Clock className="w-5 h-5 animate-spin text-indigo-600" />
                              )}
                              {isMultiUrlMode && row.status === "valid" && (
                                <CircleCheck className="w-5 h-5 text-green-600" />
                              )}
                              {isMultiUrlMode && row.status === "invalid" && (
                                <CircleX className="w-5 h-5 text-red-600" />
                              )}
                            </div>
                          )}
                          {isMultiUrlMode && (
                            <button
                              type="button"
                              onClick={() => handleRemoveJobUrlRow(row.id)}
                              className="buttons-red-mini"
                              title="Remove URL"
                            >
                              <CircleX className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {isMultiUrlMode &&
                          row.status === "valid" &&
                          row.validation?.details && (
                            <p className="text-xs text-green-700 mt-1">
                              Valid: {row.validation.details.title || "Job"} at{" "}
                              {row.validation.details.company || "company"}
                            </p>
                          )}
                        {isMultiUrlMode && row.error && (
                          <p className="text-xs text-red-600 mt-1">
                            {row.error}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  {maxPopupRows <= 1 ? (
                    <p className="text-xs text-gray-500 mt-2">
                      Your plan allows one job URL at a time.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-2">
                      You can add up to {maxPopupRows} job URLs at once.
                    </p>
                  )}
                </div>

                <div className="buttons-container">
                  {validJobRows.length === 0 && (
                    <button
                      onClick={handleValidateJobUrlRows}
                      disabled={
                        isAnyUrlValidating ||
                        !jobUrlRows.some((row) => row.url.trim())
                      }
                      className="buttons-indigo"
                    >
                      {isAnyUrlValidating ? (
                        <>
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                          Scraping info...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Scrape from URL
                        </>
                      )}
                    </button>
                  )}

                  {(validJobRows.length > 0 || isGettingJobDetails) && (
                    <div className="buttons-container">
                      <button
                        onClick={handleTailorResumeViaUrl}
                        disabled={
                          isGettingJobDetails || isSaving || isBulkProcessing
                        }
                        className="buttons-indigo"
                      >
                        {isGettingJobDetails || isBulkProcessing ? (
                          <>
                            <Clock className="w-4 h-4 mr-2 animate-spin" />
                            Tailoring...
                          </>
                        ) : (
                          <>
                            <FileUser className="w-4 h-4 mr-2" />
                            {hasMultipleValidJobs
                              ? "Tailor all"
                              : "Tailor Resume Now"}
                          </>
                        )}
                      </button>

                      <button
                        onClick={handleSaveValidJobRows}
                        disabled={isSaving}
                        className="buttons-green"
                      >
                        {isSaving ? (
                          <>
                            <Clock className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Bookmark className="w-4 h-4 mr-2" />
                            {hasMultipleValidJobs
                              ? "Save all"
                              : "Save for later"}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk action toolbar */}

        <div className="rounded-lg px-4 py-2 mb-2 flex items-center justify-between shadow-lg bg-white">
          {searchedJobs.length > 0 && (
            <>
              {selectedJobs.length > 0 ? (
                <div className="flex gap-2 justify-between items-center w-full">
                  <button
                    onClick={handleDeselectAll}
                    disabled={isBulkProcessing}
                    className="buttons-navigation"
                  >
                    Deselect All
                  </button>
                  <button
                    onClick={handleBulkTailor}
                    disabled={isBulkProcessing}
                    className="flex items-center bg-indigo-600 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    <FileEdit className="w-4 h-4 mr-2" />
                    Tailor
                  </button>

                  <button
                    onClick={handleBulkDelete}
                    disabled={isBulkProcessing}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleSelectAll}
                  disabled={isBulkProcessing}
                  className="buttons-navigation"
                >
                  Select All
                </button>
              )}
            </>
          )}
        </div>

        <div className="grid gap-4 overflow-auto max-h-66 rounded-lg">
          {isLoadingJobs ? (
            <div className="container-center h-full">
              <img
                src="/assets/Applicant-loading.png"
                className="w-25 h-25 max-auto mb-2"
                alt="Applicant loading logo"
              />
            </div>
          ) : searchedJobs.length > 0 ? (
            <div className="grid gap-4 relative">
              {searchedJobs.map((job) => (
                <div key={job.id} style={{ position: "relative" }}>
                  <JobCard
                    key={job.id}
                    job={job}
                    setJobList={setJobList}
                    onApply={handleJobApplication}
                    isLoading={isLoading}
                    isSelected={selectedJobs.includes(job.id)}
                    onToggleSelect={handleToggleSelect}
                    resumeData={resumeData}
                    setJobDescription={setJobDescription}
                    navigateTo={navigateTo}
                  />
                  {isBulkProcessing && selectedJobs.includes(job.id) && (
                    <div className="absolute bg-loading-opacity-50 top-0 z-2 w-full h-full overlaying-container rounded-lg bg-loading-color">
                      <img
                        src="/assets/Applicant-loading.png"
                        className="w-25 h-25 opacity-75 max-auto"
                        alt="Applicant loading logo"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center">
              <p className="text-gray-500">
                No saved jobs found. If you have a filter, please reset. Add a
                new custom job url to start saving and tailoring!
              </p>
              <div className="container-center h-80">
                <div className="buttons-container">
                  <button
                    onClick={() => setShowJobUrlPopup(true)}
                    className="buttons-indigo"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add your first job
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default YourJobsScreen;
